import { createCompanyPeopleHeroContent } from './companyPeopleHeroContent.js';
import { companyHeroCopy, companyObjectionsCopy } from './companyHomeContent.js';
import { createMatchingContent } from './landingMatchingContent.js';
import type { LandingContent } from './landingContentTypes.js';
import * as shared from './landingContentShared.js';
const { industryCare, industryCraft, industryGastro, heroPflege, heroGastro, heroHandwerk, heroHandel, heroUnternehmenBedarf, heroUnternehmenBranchen, heroUnternehmenDokumente, heroUnternehmenIntegration, partnerGlobus, partnerTempusleben, partnerEmek, groupPhoto, assets, contact } = shared;
import { deContent } from './landingContent.de.js';

export const enContent: LandingContent = {
  ...deContent,
  locale: 'en',
  languageLabel: 'English',
  nav: [
    { label: 'Care', href: '/branchen/pflege' },
    { label: 'Hospitality', href: '/branchen/gastro' },
    { label: 'Trades', href: '/branchen/handwerk' },
    { label: 'Retail', href: '/branchen/einzelhandel' },
  ],
  hero: {
    eyebrow: 'Responsible international recruitment',
    title: 'Viva Porta connects people',
    highlight: 'with a future',
    videoUrl: '',
    intro: 'Viva Porta connects companies in Germany with international people who want to build their professional future here. Personally guided, reliable and designed for long-term integration.',
    bullets: ['Clear industry solutions for care, hospitality, trades and retail', 'Personal guidance for companies and applicants', 'A reliable process from first conversation to arrival in Germany'],
    primaryAction: { label: 'For applicants', href: contact.application },
    secondaryAction: { label: 'View industries', href: '#branchen' },
    imageAlt: 'Viva Porta approach to human-centred international recruitment',
    trustLabels: ['Personal guidance', 'Companies and applicants', 'Long-term integration'],
  },
  heroApplicant: {
    badge: 'Join 1,500+ nurses',
    avatars: [assets.nursePortrait1, assets.nursePortrait2, assets.nursePortrait3],
    title: 'More future. More security. More you.',
    highlight: 'More you.',
    intro: 'We guide nurses from Türkiye to Germany personally – step by step.',
    trustLabel: 'Trustpilot 4.9 / 5 · 320+ reviews',
    cards: [
      {
        eyebrow: 'Language',
        title: 'Speak German with confidence',
        image: assets.nursePortrait1,
        imageAlt: 'Nurse learning German',
        href: '/ablauf',
      },
      {
        eyebrow: 'Recognition',
        title: 'Get your profession recognised',
        image: assets.nursePortrait2,
        imageAlt: 'Nurse with recognised documents',
        href: '/ablauf',
      },
      {
        eyebrow: 'Arrival',
        title: 'Settle in Germany',
        image: assets.nursePortrait3,
        imageAlt: 'Nurse in a new work environment',
        href: contact.application,
      },
    ],
  },
  heroCompany: {
    badge: 'Trusted by 40+ facilities',
    avatars: [assets.nursePortrait3, assets.nursePortrait1, assets.nursePortrait2],
    title: 'More professionals. More security. More team.',
    highlight: 'More team.',
    intro: 'Verified nursing professionals from Turkey – legally secure placement with full support.',
    trustLabel: 'Legally secure · Recognition support included',
    cards: [
      {
        eyebrow: 'Professionals',
        title: 'Verified nurses',
        image: assets.nursePortrait2,
        imageAlt: 'Verified nursing professional',
        href: contact.medical,
      },
      {
        eyebrow: 'Secure',
        title: 'Hassle-free hiring',
        image: assets.nursePortrait1,
        imageAlt: 'Nurse in clinical setting',
        href: '#kontakt',
      },
      {
        eyebrow: 'Support',
        title: 'Fully supported',
        image: assets.nursePortrait3,
        imageAlt: 'Nurse receiving support',
        href: '#ablauf',
      },
    ],
  },
  heroFanApplicant: {
    badge: 'For international applicants',
    avatars: [industryCare, industryGastro, industryCraft],
    title: 'Your professional start in Germany begins here.',
    highlight: 'begins here.',
    intro: 'You want to work in Germany and need a serious path forward? Viva Porta shows you suitable industries, explains the next steps and personally supports you from orientation to arrival.',
    trustLabel: '',
    cards: [
      {
        eyebrow: 'Care',
        title: 'Start in care',
        image: heroPflege,
        imageAlt: 'Care professional in a bright modern facility',
        href: '/branchen/pflege',
      },
      {
        eyebrow: 'Hospitality',
        title: 'Work in service or kitchen',
        image: heroGastro,
        imageAlt: 'Hospitality employee in a modern restaurant',
        href: '/branchen/gastro',
      },
      {
        eyebrow: 'Trades',
        title: 'Build your path in trades',
        image: heroHandwerk,
        imageAlt: 'Skilled trades worker in a bright workshop',
        href: '/branchen/handwerk',
      },
      {
        eyebrow: 'Retail',
        title: 'Help customers in retail',
        image: heroHandel,
        imageAlt: 'Retail employee in a modern store',
        href: '/branchen/einzelhandel',
      },
    ],
  },
  heroFanCompany: {
    badge: 'For employers in Germany',
    avatars: [industryCare, industryGastro, industryCraft],
    title: 'Hire international people who truly fit your team.',
    highlight: 'fit your team.',
    intro: 'Looking for reliable international staff? Viva Porta clarifies your demand, checks profiles, coordinates formalities and supports arrival in your operation.',
    trustLabel: '',
    cards: [
      {
        eyebrow: 'Demand',
        title: 'Clarify staffing needs',
        image: heroUnternehmenBedarf,
        imageAlt: 'Consultation meeting with an employer',
        href: '#ablauf',
        quote: 'The first conversation made clear which profiles were realistic and which steps our team had to prepare.',
        quoteAuthor: 'HR lead',
        quoteRole: 'Mid-sized employer',
      },
      {
        eyebrow: 'Industries',
        title: 'Profiles for real roles',
        image: heroUnternehmenBranchen,
        imageAlt: 'Service team in a modern hospitality business',
        href: '#branchen',
        quote: 'We did not receive anonymous CVs, but clearly pre-checked profiles that matched our day-to-day operations.',
        quoteAuthor: 'Operations lead',
        quoteRole: 'Hospitality and service',
      },
      {
        eyebrow: 'Formalities',
        title: 'Documents coordinated',
        image: heroUnternehmenDokumente,
        imageAlt: 'Documents and digital planning for international hiring',
        href: '#ablauf',
        quote: 'Recognition, visa and paperwork were transparently structured. Our internal team always knew the next step.',
        quoteAuthor: 'People lead',
        quoteRole: 'Care provider',
      },
      {
        eyebrow: 'Integration',
        title: 'Support arrival in the team',
        image: heroUnternehmenIntegration,
        imageAlt: 'International professional welcomed into a team',
        href: '#kontakt',
        quote: 'The support after the start helped us solve questions early and bring new colleagues into the team sustainably.',
        quoteAuthor: 'Facility lead',
        quoteRole: 'Employer in Germany',
      },
    ],
  },
  companyPlatformHero: { ...companyHeroCopy.en, matching: createMatchingContent('en') },
  companyPeopleHero: createCompanyPeopleHeroContent('en'),
  companyObjections: companyObjectionsCopy.en,
  intro: {
    eyebrow: 'Welcome',
    title: 'Your path to Germany, clearly guided and personally supported.',
    titleHighlight: 'supported',
    text: 'We combine Viva Porta’s experience in visa and relocation processes with in-depth knowledge of international career pathways. The result is a structured path for people who want to build a professional future in Germany.',
    metrics: [
      { value: '360°', label: 'Support for language, recognition, visa and arrival' },
      { value: '2', label: 'Two cultures, one team: Germany and Türkiye' },
      { value: '24/7', label: 'Reachable support in critical phases' },
    ],
  },
  reasons: {
    eyebrow: 'Why Viva Porta?',
    title: 'Strong reasons for a nursing career in Germany.',
    titleHighlight: 'Germany',
    image: assets.article,
    text: 'Germany offers security, modern healthcare structures and real career perspectives. Viva Porta turns that opportunity into a plannable new start.',
    cards: [
      {
        iconClass: 'icon-[material-symbols--groups]',
        title: 'Strong community',
        text: 'A large Turkish community in Germany makes everyday life, work and social integration easier.',
      },
      {
        iconClass: 'icon-[material-symbols--flight-takeoff]',
        title: 'Close to home',
        text: 'Many direct connections between Germany and Türkiye help keep family and your new life close together.',
      },
      {
        iconClass: 'icon-[material-symbols--verified-user]',
        title: 'Security and fairness',
        text: 'We focus on fair conditions, transparent communication and placements that match your profile.',
      },
    ],
  },
  benefits: {
    eyebrow: 'Benefits at a glance',
    title: 'What you can expect from us.',
    titleHighlight: 'us',
    items: [
      {
        iconClass: 'icon-[material-symbols--school]',
        title: 'Language and development',
        text: 'Guidance toward the right language level, including preparation for healthcare communication.',
      },
      {
        iconClass: 'icon-[material-symbols--approval-delegation]',
        title: 'Recognition and documents',
        text: 'Support with documents, translations, recognition procedures and communication with authorities.',
      },
      {
        iconClass: 'icon-[material-symbols--home-work]',
        title: 'Relocation',
        text: 'Help with arrival, accommodation, registration, residence questions and the first steps on site.',
      },
      {
        iconClass: 'icon-[material-symbols--support-agent]',
        title: 'Personal support',
        text: 'A bilingual team supports you before, during and after your arrival in Germany.',
      },
    ],
  },
  costs: {
    eyebrow: 'Free for applicants',
    title: 'What does your path to Germany cost?',
    titleHighlight: 'cost',
    text: 'The application and initial consultation with Viva Porta are free of charge. We work transparently: you receive a clear overview of upcoming steps and which costs may be covered by partners or employers.',
    includedTitle: 'We coordinate for you',
    excludedTitle: 'No barrier at first contact',
    included: ['Application check', 'Language and recognition planning', 'Visa and relocation roadmap'],
    excluded: ['No application fee', 'No unclear advance payments', 'No hidden process steps'],
  },
  recognition: {
    eyebrow: 'Language and recognition',
    title: 'Two key building blocks for your professional start.',
    titleHighlight: 'start',
    text: 'German language skills and recognition of your qualification are central to long-term employment in nursing. We guide you through both topics step by step.',
    cards: [
      {
        iconClass: 'icon-[material-symbols--translate]',
        title: 'Language pathway',
        text: 'Individual guidance for German courses, exams and professional healthcare language.',
      },
      {
        iconClass: 'icon-[material-symbols--workspace-premium]',
        title: 'Professional recognition',
        text: 'Review of your documents and support until the responsible authority reaches a decision.',
      },
      {
        iconClass: 'icon-[material-symbols--clinical-notes]',
        title: 'Preparation',
        text: 'Cultural workshops, interview training and practical preparation for day-to-day work in the team.',
      },
    ],
  },
  process: {
    eyebrow: 'Your path to Germany',
    title: 'From the first call to your start in the new team.',
    titleHighlight: 'new team',
    text: 'Moving to Germany is a big step, but it does not have to feel confusing. We guide you through application, employer interviews, language, recognition, visa, entry and settling in.',
    steps: [
      {
        iconClass: 'icon-[material-symbols--person-search]',
        title: 'First consultation',
        text: 'We get to know your training, experience, goals and current German level and give you an honest assessment.',
      },
      {
        iconClass: 'icon-[material-symbols--inventory]',
        title: 'Documents and profile',
        text: 'Together we check certificates, proof of qualification and documents and prepare your profile for suitable employers in Germany.',
      },
      {
        iconClass: 'icon-[material-symbols--forum]',
        title: 'Employer interviews',
        text: 'We suggest suitable employers, prepare you for interviews and support you until a decision is made.',
      },
      {
        iconClass: 'icon-[material-symbols--translate]',
        title: 'Language',
        text: 'You learn German with a clear goal: everyday life, interviews, nursing terminology and the level needed for recognition and work.',
      },
      {
        iconClass: 'icon-[material-symbols--workspace-premium]',
        title: 'Recognition and visa',
        text: 'We support translations, recognition application, appointments, visa documents and coordination with authorities.',
      },
      {
        iconClass: 'icon-[material-symbols--flight-land]',
        title: 'Entry and arrival',
        text: 'Before you travel, we clarify arrival, accommodation, registration and the first days so you can start safely in Germany.',
      },
    ],
  },
  team: {
    ...deContent.team,
    eyebrow: 'Our founding team',
    title: 'Expertise, network and personal guidance.',
    titleHighlight: 'guidance',
    text: 'Viva Porta combines entrepreneurial experience, a German-Turkish perspective and expertise in international career pathways.',
    members: [
      {
        name: 'Enes Vural',
        role: 'Managing Director',
        text: 'Connects applicants, employers and the real questions that come with integration.',
        story: 'Enes understands both sides of placement: what German employers need and what international applicants need to know before starting a new life in Germany. He makes sure the process stays personal, clear and realistic.',
        motivation: 'He wants fair opportunities to become sustainable paths for applicants and reliable hiring decisions for employers.',
        image: assets.teamEnes,
        linkedinUrl: 'https://de.linkedin.com/in/enes-vural-1348262b7',
      },
      {
        name: 'Wilhelm Josten',
        role: 'Shareholder and Advisor',
        text: 'Contributes strategic experience, network and a calm view on resilient structures.',
        story: 'Wilhelm sees international recruitment as more than filling vacancies. He supports Viva Porta in building partnerships, processes and quality standards that remain reliable as the company grows.',
        motivation: 'He invests in Viva Porta because international recruitment only makes sense when it is human, commercially sound and well organised.',
        image: assets.teamWilhelm,
        linkedinUrl: 'https://de.linkedin.com/in/wilhelm-josten-6bb14930a',
      },
      {
        name: 'Serife Vural-Banik',
        role: 'Operations Lead',
        text: 'Supports candidates bilingually, carefully and with a strong sense for sensitive process moments.',
        story: 'Serife keeps the daily process moving: application questions, documents, expectations, follow-ups and personal uncertainties. She creates clarity when decisions feel big.',
        motivation: 'Her focus is to translate complexity into understandable next steps and build trust throughout the journey.',
        image: assets.teamSerife,
      },
      {
        name: 'Murat Vuval',
        role: 'Shareholder and Advisor',
        text: 'Brings an entrepreneurial perspective and a focus on stable, fair partnerships.',
        story: 'Murat helps evaluate opportunities realistically and think partnerships through for the long term. His perspective supports balanced decisions for applicants and employers.',
        motivation: 'He cares that placement does not end with a signed contract, but becomes participation and a lasting working relationship.',
        image: assets.teamMurat,
        linkedinUrl: 'https://www.linkedin.com/in/murat-v-113bb912/',
      },
      {
        name: 'Dr. Kirstin Wingler',
        role: 'Executive Assistant',
        text: 'Supports processes, specialist questions and coordination with medical understanding and precision.',
        story: 'Kirstin combines scientific precision with practical organisation. She helps classify documents, qualifications and professional requirements early and clearly.',
        motivation: 'She turns complex specialist topics into understandable process steps and keeps details moving in the right order.',
        image: assets.teamKirstin,
        linkedinUrl: 'https://www.linkedin.com/in/kirstin-w-43264a4/',
      },
      {
        name: 'Ergün Yalcin',
        role: 'Administration',
        text: 'Keeps administrative workflows organised, reliable and calm in the background.',
        story: 'Ergün supports data, documents, coordination and follow-up wherever accuracy matters. He keeps the administrative foundation stable while many steps run in parallel.',
        motivation: 'His aim is that applicants and partners can rely on clear, well-kept processes.',
        image: assets.teamErgun,
      },
    ],
  },
  story: {
    ...deContent.story,
    eyebrow: 'Success story',
    title: 'A prepared space for real feedback.',
    titleHighlight: 'feedback',
    text: 'This section can be edited with approved feedback from candidates, employers and partners, including image and logo.',
    quote: 'From the first contact to arrival, the process was transparent. I always knew the next step and had a team that understood my language and my goals.',
    author: 'Viva Porta candidate',
    role: 'Nurse from Türkiye',
    partnerLogo: assets.partner,
    partnerSecondaryLogo: assets.partnerSecondary,
  },
  partners: {
    eyebrow: 'Partners & network',
    title: 'Trust built on real partnerships.',
    titleHighlight: 'partnerships',
    text: 'Selected companies and networks across Germany and Türkiye that power our work.',
    trustLabel: 'Member of the GVP – German Federation of Staffing Providers',
    items: [
      {
        name: 'Globus',
        logo: partnerGlobus,
        alt: 'Globus logo',
        description: 'For full-range retailers like Globus we recruit specialists for the fresh-food counters – butchery, fish and cheese. Exactly where expert advice drives footfall, enables margins and builds lasting customer loyalty.',
        href: '/globus',
      },
      {
        name: 'tempusleben',
        logo: partnerTempusleben,
        alt: 'tempusleben logo',
        description: '“Viva Porta is an exceptionally committed business partner with outstanding contacts in the Turkish labour market. Give the team a chance to show what is possible.” – Gerd Schneider, Managing Director of tempusleben.',
        href: 'https://tempusleben.de/',
      },
      {
        name: 'EMEK',
        logo: partnerEmek,
        alt: 'EMEK logo',
        description: 'EMEK GmbH is a nursing care provider. As a partner from the care environment, EMEK brings practical understanding, proximity to daily work and a network that prepares people instead of merely placing them.',
        href: 'https://emek-pflegedienst.de/',
      },
    ],
  },
  globusPartner: {
    eyebrow: 'Retail partner',
    title: 'A new start in retail with Globus.',
    text: 'Globus stands for large, service-oriented stores with strong fresh-food departments. Together we create a framework in which international talent can be prepared, supported and integrated into teams with clear expectations and personal guidance.',
    image: groupPhoto,
    imageAlt: 'Viva Porta visiting the butchery department of a GLOBUS store',
    logo: partnerGlobus,
    logoAlt: 'Globus logo',
    primaryAction: { label: 'View retail solution', href: '/branchen/einzelhandel' },
  },
  info: {
    eyebrow: 'Additional resources',
    title: 'Important information about immigration, recognition and nursing in Germany.',
    titleHighlight: 'Germany',
    links: deContent.info.links,
  },
  contact: {
    eyebrow: 'Bir sonraki adıma hazır mısın?',
    title: 'Apply to work as a nurse in Germany.',
    titleHighlight: 'Germany',
    text: 'Send us your inquiry. We will get back to you with a realistic assessment and the next steps for your path.',
    primaryAction: { label: 'Apply for free', href: contact.application },
    secondaryAction: { label: 'Send email', href: contact.mail },
    address: 'Viva Porta GmbH, Germany',
    email: 'info@vivaporta.org',
    hours: 'Mon - Fri: 9:00 - 17:00',
  },
  requirements: {
    eyebrow: 'Requirements',
    title: 'What you need to start your journey to Germany.',
    titleHighlight: 'Germany',
    text: 'You don’t have to manage everything alone. You bring your training and motivation – we organise the rest together, step by step.',
    columns: [
      {
        iconClass: 'icon-[material-symbols--person-check]',
        title: 'What you bring',
        items: [
          'Completed nursing training or a nursing degree from Turkey',
          'Motivation to learn German to at least level B1/B2',
          'Willingness to relocate to Germany for your new career',
          'A valid passport and complete proof of qualification',
        ],
      },
      {
        iconClass: 'icon-[material-symbols--volunteer-activism]',
        title: 'What we take care of',
        items: [
          'Free German courses up to the certified language level',
          'Recognition of your qualification incl. translations and authorities',
          'Visa, entry and the search for your first apartment',
          'Personal support from the first call until after your arrival',
        ],
      },
    ],
  },
  faqApplicant: {
    eyebrow: 'FAQ for nurses',
    title: 'The most important questions before you apply.',
    titleHighlight: 'apply',
    text: 'If your question isn’t answered here, just call us – we advise you free of charge and in Turkish.',
    items: [
      {
        question: 'What does the placement cost me?',
        answer: 'Your employer covers most of the fees on your behalf. This means that placement is generally free for you. Depending on your profession and industry, however, consultation fees may apply. If you would like to know more, please contact us.',
      },
      {
        question: 'Which language level do I need?',
        answer: 'An employer in Germany will naturally expect you to speak German in the long term because you need to communicate with colleagues and customers. Depending on your profession, however, you may be able to enter Germany without perfect German. Important: Care professionals need at least B1 German for their visa.',
      },
      {
        question: 'Will my Turkish qualification be recognised?',
        answer: 'We support you throughout the entire recognition process and assess in advance whether, and under which conditions, your qualification can be recognised in Germany.',
      },
      {
        question: 'How long does the whole journey take?',
        answer: 'Unfortunately, there is no one-size-fits-all answer because the timeline depends heavily on the responsible authorities. Processing times vary between Germany’s federal states, and every procedure is individual. Based on our experience, it takes around three to twelve months, depending on the profession, before you can enter Germany.',
      },
      {
        question: 'Do I get an employment contract beforehand?',
        answer: 'Of course. An employment contract is an important prerequisite for starting the recognition process and applying for a visa.',
      },
      {
        question: 'After I arrive, do you also help with authorities and initial accommodation?',
        answer: 'Yes. We accompany you during your first appointments with the authorities and help you arrange important insurance policies and contracts. We also support you in finding accommodation.',
      },
    ],
  },
  faqCompany: {
    eyebrow: 'FAQ for employers',
    title: 'Answers for clinics and care facilities.',
    titleHighlight: 'care facilities',
    text: 'Do you have further questions about the process, costs or legal aspects? Talk directly to our team.',
    items: [
      {
        question: 'What does the placement cost?',
        answer: 'You pay on a success basis – costs only arise upon successful placement. No hidden fees, a transparent model at eye level.',
      },
      {
        question: 'What qualifications do the nurses have?',
        answer: 'All candidates are registered nurses with a structured pre-selection, certified language level B1/B2 and an ongoing or completed recognition procedure.',
      },
      {
        question: 'Do you handle recognition and bureaucracy?',
        answer: 'Yes. We fully manage documents, translations, the recognition application, visa and work permit, noticeably relieving your team.',
      },
      {
        question: 'How long until the start of work?',
        answer: 'Usually 12 to 18 months from the assignment – including language, recognition and entry. You get fixed contacts and transparent timelines.',
      },
      {
        question: 'What happens after arrival?',
        answer: 'We support onboarding and integration beyond the first working day and remain your contact for a sustainably successful start.',
      },
      {
        question: 'Why nurses from Turkey?',
        answer: 'Turkey trains nurses to a high standard. Cultural proximity, a large community in Germany and short direct flights ensure faster integration and a high retention rate.',
      },
    ],
  },
  quality: {
    eyebrow: 'Quality and Integration',
    title: 'Fair placement that lasts within your team.',
    titleHighlight: 'lasts',
    text: 'For us, successful placement doesn’t end at the airport. Proven quality and real integration ensure your new nurses stay long term.',
    stats: [
      { value: '95%', label: 'Retention rate of placed professionals' },
      { value: 'B1/B2', label: 'Certified language level before the first working day' },
      { value: '24/7', label: 'Support during the decisive starting phase' },
    ],
    points: [
      {
        iconClass: 'icon-[material-symbols--verified]',
        title: 'Fair recruitment',
        text: 'We work according to fair recruitment principles – transparent, legally compliant and in line with WHO guidelines.',
      },
      {
        iconClass: 'icon-[material-symbols--diversity-3]',
        title: 'Cultural integration',
        text: 'Intercultural workshops and personal support help professionals settle quickly into the team and everyday life.',
      },
      {
        iconClass: 'icon-[material-symbols--health-and-safety]',
        title: 'Quality-checked',
        text: 'Professional aptitude, language certificate and recognition procedure are documented transparently before placement.',
      },
      {
        iconClass: 'icon-[material-symbols--handshake]',
        title: 'Long-term commitment',
        text: 'Aftercare and onboarding support reduce turnover and secure the lasting success of the hire.',
      },
    ],
  },
  knowledge: {
    eyebrow: 'Knowledge and Guide',
    title: 'Well informed on your way to Germany.',
    titleHighlight: 'informed',
    text: 'Answers and orientation on the most important topics around recognition, language and life in Germany.',
    articles: [
      {
        iconClass: 'icon-[material-symbols--workspace-premium]',
        eyebrow: 'Recognition',
        title: 'Recognition in Germany',
        text: 'How your Turkish nursing qualification is recognised – from application to decision.',
        linkLabel: 'Learn more',
        href: 'https://www.anerkennung-in-deutschland.de/',
      },
      {
        iconClass: 'icon-[material-symbols--translate]',
        eyebrow: 'Language',
        title: 'Learning German for nurses',
        text: 'From everyday German to medical terminology – how you reach your language goal.',
        linkLabel: 'Learn more',
        href: '/ablauf',
      },
      {
        iconClass: 'icon-[material-symbols--home-work]',
        eyebrow: 'Life',
        title: 'Living and working in Germany',
        text: 'Housing, authorities, salary and daily life: what awaits you in your new home.',
        linkLabel: 'Learn more',
        href: 'https://www.make-it-in-germany.com/',
      },
      {
        iconClass: 'icon-[material-symbols--balance]',
        eyebrow: 'Fairness',
        title: 'Fair placement',
        text: 'How to recognise a fair, reputable placement – and what to look out for.',
        linkLabel: 'Learn more',
        href: '/ueber-vivaporta',
      },
    ],
  },
  values: {
    eyebrow: 'Our values',
    title: 'Why we connect Turkey and Germany.',
    titleHighlight: 'connect',
    text: 'Viva Porta stands for placement at eye level – with clear values that guide every decision.',
    items: [
      {
        iconClass: 'icon-[material-symbols--handshake]',
        title: 'Fairness',
        text: 'Transparent conditions, no hidden costs and a placement that truly fits both person and position.',
      },
      {
        iconClass: 'icon-[material-symbols--diversity-1]',
        title: 'Humanity',
        text: 'Behind every placement is a life journey. We provide personal, bilingual support with genuine understanding.',
      },
      {
        iconClass: 'icon-[material-symbols--verified-user]',
        title: 'Reliability',
        text: 'Clear stages, fixed contacts and support beyond the first working day.',
      },
    ],
    bridgeTitle: 'Why Turkey and Germany?',
    bridgeText:
      'Turkey trains highly qualified nurses while Germany urgently needs professionals. A large Turkish community, cultural proximity and short direct flights make the path human and practical – for professionals and employers alike.',
  },
  consultationApplicant: {
    eyebrow: 'Request a first consultation',
    title: 'Start with a personal conversation.',
    titleHighlight: 'personal',
    text: 'Tell us about your goal. In a free first conversation – in Turkish if you like – we clarify your requirements and the next steps.',
    phoneLabel: 'Call directly',
    phoneNumber: 'tel:+4917642887719',
    phoneDisplay: '+49 176 42887719',
    primaryAction: { label: 'Call now', href: 'tel:+4917642887719' },
    secondaryAction: { label: 'Register online', href: contact.application },
    trustLabel: 'Free & non-binding · Reply within 24 hours',
  },
  consultationCompany: {
    eyebrow: 'Discuss your staffing needs',
    title: 'Let’s discuss your staffing needs.',
    titleHighlight: 'staffing needs',
    text: 'Tell us which nurses you are looking for. In a non-binding conversation we show which profiles are available and how quickly we can start.',
    phoneLabel: 'Call directly',
    phoneNumber: 'tel:+4917642887719',
    phoneDisplay: '+49 176 42887719',
    primaryAction: { label: 'Call now', href: 'tel:+4917642887719' },
    secondaryAction: { label: 'Request consultation', href: contact.medical },
    trustLabel: 'Success-based · Fixed contacts · Reply within 24 hours',
  },
  footer: {
    text: 'Viva Porta connects companies in Germany with international people through a fair, transparent and personal process.',
    links: [
      { label: 'Home', href: '/' },
      { label: 'Care', href: '/branchen/pflege' },
      { label: 'Hospitality', href: '/branchen/gastro' },
      { label: 'Trades', href: '/branchen/handwerk' },
      { label: 'Retail', href: '/branchen/einzelhandel' },
      { label: 'About Viva Porta', href: '/ueber-vivaporta' },
      { label: 'Contact', href: '/kontakt' },
      { label: 'Legal notice', href: '/impressum' },
    ],
    socialLinks: deContent.footer.socialLinks,
  },
};
